<template>
	<div v-if="data" class="list_item_wrap">
		<div v-for="item in data" :key="item.etf_pk" class="list_item">
			<div class="bookmark_area" @click="onClickBookmark(item.etf_event_pk, item.bookmark, item.etf_event_color)">
				<IconThinStar :icon-color="!item.bookmark ? '#D9D9D9' : '#FFB800'" />
			</div>
			<div class="item_content">
				<div class="content">
					<div
						class="content_img"
						:style="{
							backgroundColor: item.etf_event_color,
							color: getTextColorForBackground(item.etf_event_color ?? ''),
						}"
					>
						<div class="event_ticker">
							{{ item.etf_event_ticker }}
						</div>
					</div>
					<div class="content_txt">
						<h4>{{ item.etf_event_name }}</h4>
					</div>
				</div>
				<button
					class="c_button primary-fill radius-full btn-research"
					:style="{ marginRight: '1.6rem' }"
					@click="onClickResearch(item.etf_pk, item.etf_event_pk, item.etf_event_color)"
				>
					분석
				</button>
			</div>
		</div>
	</div>
	<div v-else class="list_item_wrap">
		<div v-for="item in favoriteData" :key="item.etf_pk" class="list_item">
			<div class="bookmark_area" @click="onClickBookmark(item.etf_event_pk ?? '', Number(item.bookmark))">
				<IconThinStar :icon-color="!item.bookmark ? '#D9D9D9' : '#FFB800'" />
			</div>
			<div class="item_content">
				<div class="content">
					<div
						class="content_img"
						:style="{
							backgroundColor: item.event_color,
							color: getTextColorForBackground(item.event_color ?? ''),
						}"
					>
						<span>{{ item.event_ticker }}</span>
					</div>
					<div class="content_txt">
						<h4>{{ item.event_name }}</h4>
					</div>
				</div>
				<button
					class="c_button primary-fill radius-full btn-research"
					:style="{ marginRight: '1.6rem' }"
					@click="onClickResearch(item.etf_pk, item.etf_event_pk, item.event_color)"
				>
					분석
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
// core
import { useRouter } from 'vue-router';

// api
import { Etf } from '@/api/etf';
import { useEtfAdjustBookmarksApi } from '@/api/Interest';
import { MonthlyCapture } from '@/api/master';
// constant
import {
	CANCEL,
	COMPANY_TITLE,
	CONFIRM_LOGIN,
	LOGIN_REQUIRED_PAGE,
	MESSAGE_CANCEL_COMPLETED,
	UNFAVORITE_PROMPT,
} from '@/constants/title';
import { useLoginPopupStore } from '@/stores/LoginPopup';
// store
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
import { useTokenStore } from '@/stores/token';
import { getTextColorForBackground } from '@/utils/function/convertValue';

import IconThinStar from '../atom/icon/IconThinStar.vue';

// -----variable-----
defineProps<{
	data?: Etf[];
	favoriteData?: MonthlyCapture[];
}>();
const emit = defineEmits(['update:bookMarkRefetch', 'update:research']);

const popupStore = usePopupStore();
const toastStore = useToastStore();
const tokenStore = useTokenStore();
const loginPopupStore = useLoginPopupStore();
const router = useRouter();

const { mutateAsync: adjustBookmarks } = useEtfAdjustBookmarksApi();

// // -----function-----
const onClickBookmark = async (pk: number | string, bookmark: number, color?: string) => {
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}

	if (bookmark) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: UNFAVORITE_PROMPT,
			confirm: async () => {
				const res = await adjustBookmarks({
					etf_event_pk: pk,
					color: color ?? '',
				});
				if (res.data.success) {
					emit('update:bookMarkRefetch');
					toastStore.openToast({
						subContent: COMPANY_TITLE,
						content: MESSAGE_CANCEL_COMPLETED,
					});
				}
			},
		});
	} else {
		const res = await adjustBookmarks({
			etf_event_pk: pk,
			color: color ?? '',
		});
		if (res.data.success) {
			emit('update:bookMarkRefetch');
		}
	}
};

const onClickResearch = async (pk?: number, eventPk?: number, color?: string) => {
	router.push({ path: `/etf/detail/${pk}/${eventPk}`, query: { color: color } });
	emit('update:research');
};
</script>

<style scoped lang="scss">
.list_item_wrap {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}
.list_item {
	display: flex;
	.bookmark_area {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 0.8rem;
		border-radius: 8px 0px 0px 8px;
		background: #2c3646;
		cursor: pointer;
	}
	.item_content {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-radius: 0px 8px 8px 0px;
		background: #212c3f;
		font-size: var(--font-s-mid);
		font-weight: var(--font-w-semi-bold);
		line-height: normal;
		min-width: 0;
		overflow: hidden;
		.content {
			display: flex;
			align-items: center;
			gap: 0.8rem;
			padding: 1.6rem 1rem;
			.content_img {
				display: flex;
				justify-content: center;
				align-items: center;
				width: 5.6rem;
				height: 5.6rem;
				border-radius: 50%;
				text-align: center;
				font-size: 1.2rem;
				font-weight: var(--font-w-semi-bold);
				line-height: 1.3rem;
				flex-shrink: 0;
			}
			.content_txt {
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical;
			}
			.btn-research {
				display: flex;
				min-width: 4rem;
				height: auto;
				padding: 0.6rem 1.6rem;
				justify-content: center;
				align-items: center;
				gap: 1rem;
			}
		}
	}
}
</style>
